import { useState } from 'react'
import { generateGhScript, packageGhFile } from '../../services/api'

// design.osteo3d.com "GH Script Generator": describe a parametric component in
// plain language → Claude (server-side) writes Rhino 8 Python 3 code. Download a
// ready-to-open Grasshopper .gh (code baked into a component), or the code / .py
// to paste into a Py3 component yourself.

// One-click starting points. The first matches the standard Osteo3d .gh box
// exactly (canonical Srf/D/G/B/Seed/Iter/Count inputs); the others show the tool
// is general-purpose (their inputs differ, so the box's params need renaming).
const EXAMPLES = [
  {
    label: 'Surface perforation',
    spec:
      'Generate uniform perforation holes on any trimmed NURBS surface or Brep — a regular ' +
      'hexagonal lattice with constant edge-to-edge spacing (every hole equidistant from its ' +
      'six neighbours) measured in true 3D, a boundary offset, and an optional hole-count cap. ' +
      'Centre-to-centre spacing is always D + G; Count only caps how many holes are kept and ' +
      'never changes the spacing. ' +
      'Inputs: Srf, D (diameter), G (gap), B (boundary offset), Seed, Iter, Count. ' +
      'Outputs: P (centre points), Pl (tangent planes), C (circles).',
  },
  {
    label: 'Hex panels',
    spec:
      'Panelize a trimmed surface into a hexagonal grid. Inputs: Srf (surface/Brep), CellSize ' +
      '(hex width, measured in true 3D), Margin (keep cells off the edge). Outputs: the hex cell ' +
      'boundary curves lying on the surface, and their centre points.',
  },
  {
    label: 'Point scatter',
    spec:
      'Evenly scatter attachment points across a trimmed surface with a minimum spacing and a ' +
      'boundary margin, deterministic by seed. Inputs: Srf, Count, MinDist, Margin, Seed. ' +
      'Outputs: P (points) and Pl (tangent planes at each point).',
  },
  {
    label: 'Lattice infill',
    spec:
      'Fill a closed Brep / solid with a periodic gyroid-style lattice strut network clipped to ' +
      'the solid. Inputs: Solid (Brep), CellSize, StrutRadius. Output the lattice as strut curves ' +
      '(or a mesh) contained within the solid.',
  },
]

export default function GhScriptTool() {
  const [spec, setSpec]     = useState('')
  const [extra, setExtra]   = useState('')
  const [code, setCode]     = useState('')
  const [model, setModel]   = useState('')
  const [busy, setBusy]     = useState(false)
  const [err, setErr]       = useState('')
  const [copied, setCopied] = useState(false)
  const [ghErr, setGhErr]   = useState('')
  const [showHelp, setShowHelp] = useState(false)

  async function generate() {
    if (!spec.trim() || busy) return
    setBusy(true); setErr(''); setGhErr(''); setCode('')
    try {
      const { data } = await generateGhScript(spec.trim(), extra.trim() || undefined)
      setCode(data.code || '')
      setModel(data.model || '')
    } catch (e) {
      setErr(e.response?.data?.error || e.message || 'Generation failed')
    } finally {
      setBusy(false)
    }
  }

  async function copyCode() {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch { /* clipboard blocked — user can select manually */ }
  }

  function download(bytes, name, type) {
    const blob = new Blob([bytes], { type })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = name
    a.click()
    URL.revokeObjectURL(url)
  }

  function downloadPy() {
    download(code, 'osteo3d-generated.py', 'text/x-python')
  }

  async function downloadGh() {
    setGhErr('')
    try {
      const { data } = await packageGhFile(code)
      download(data, 'osteo3d-generated.gh', 'application/octet-stream')
    } catch (e) {
      // Backend returns arraybuffer; decode the JSON error body.
      let msg = e.message
      try {
        const body = e.response?.data
        if (body) msg = JSON.parse(new TextDecoder().decode(body)).error || msg
      } catch { /* keep msg */ }
      setGhErr(msg)
    }
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800">Grasshopper Script Generator</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Describe a parametric component — get a ready-to-open Grasshopper{' '}
          <span className="font-medium">.gh</span> (code baked into a component), or the{' '}
          <span className="font-medium">Rhino 8 Python 3</span> code to paste yourself. Targets
          RhinoCommon / CPython 3, not Rhino 7 IronPython.
        </p>
      </div>

      {/* How to use — collapsible, distilled from docs/grasshopper/README.md */}
      <div className="bg-brand-50 border border-brand-100 rounded-xl">
        <button
          onClick={() => setShowHelp((v) => !v)}
          className="w-full flex items-center justify-between px-4 py-3 text-left"
        >
          <span className="text-sm font-semibold text-brand-900">
            How to use this in Rhino 8 / Grasshopper
          </span>
          <span className="text-brand-500 text-sm">{showHelp ? 'Hide ▲' : 'Show ▼'}</span>
        </button>
        {showHelp && (
          <div className="px-4 pb-4 space-y-4 text-sm text-gray-700">
            {/* Path 1 — .gh */}
            <div>
              <div className="font-semibold text-gray-800">Easiest — Download .gh</div>
              <ol className="list-decimal ml-5 mt-1 space-y-1">
                <li>Open <span className="font-medium">Rhino 8</span> (licensed as usual), then start Grasshopper (type <code className="bg-white px-1 rounded border">Grasshopper</code> in Rhino's command line).</li>
                <li>In Grasshopper: <span className="font-medium">File → Open</span> → the downloaded <code className="bg-white px-1 rounded border">osteo3d-generated.gh</code>.</li>
                <li>Place a <span className="font-medium">Surface</span> param → right-click → <span className="font-medium">Set one Surface</span> → pick your model in Rhino → wire it into <code className="bg-white px-1 rounded border">Srf</code>. Use a <span className="font-medium">trimmed</span> surface so results stay inside the shape.</li>
                <li>Move the sliders. Read the <code className="bg-white px-1 rounded border"># @input</code> lines at the top of the code to know what each one controls.</li>
              </ol>
              {/* The perforation generator is a hex lattice, so the old advice to tune
                  Iter for uniformity is wrong — Iter has been inert since that rewrite,
                  and Count no longer drives spacing (v0.16.16). */}
              <p className="mt-1.5 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-3 py-2">
                <span className="font-medium">Surface perforation — what the sliders actually do.</span>{' '}
                Hole spacing is <span className="font-medium">always centre-to-centre = D + G</span>, so
                widen <code className="bg-white px-1 rounded border">G</code> for fewer, further-apart holes.
                <code className="bg-white px-1 rounded border ml-1">Count</code> is only a{' '}
                <span className="font-medium">cap</span> — it thins the finished lattice and never changes the
                spacing (set <code className="bg-white px-1 rounded border">0</code> for no cap).
                <code className="bg-white px-1 rounded border ml-1">Seed</code> just rotates the lattice, and
                <code className="bg-white px-1 rounded border ml-1">Iter</code> is inert — it is kept only so
                the component drops into the existing box. The panel prints the effective spacing and whether
                the cap was applied.
              </p>
              <p className="mt-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                The <span className="font-medium">.gh</span> box comes wired with the Surface-Perforation inputs
                (<code className="bg-white px-1 rounded border">Srf, D, G, B, Seed, Iter, Count</code>). If you generated a
                <span className="font-medium"> different</span> component, its inputs may differ — rename the box's input params
                to match the <code className="bg-white px-1 rounded border"># @input</code> names at the top of the code
                (double-click the param's name letter to rename).
              </p>
            </div>

            {/* Path 2 — code */}
            <div>
              <div className="font-semibold text-gray-800">Full control — Copy code / Download .py</div>
              <ol className="list-decimal ml-5 mt-1 space-y-1">
                <li>In Grasshopper: <span className="font-medium">Maths → Script → Python 3 Script (Py3)</span>. Double-click it, paste the code, save, close.</li>
                <li><span className="font-medium">Add the inputs by hand.</span> A fresh Py3 component starts with <code className="bg-white px-1 rounded border">x, y → out</code>; pasting does <span className="font-medium">not</span> create inputs. Zoom in, use the small <code className="bg-white px-1 rounded border">+</code> on the input/output columns, and <span className="font-medium">rename</span> each (double-click the name letter) to the <code className="bg-white px-1 rounded border"># @input</code> / <code className="bg-white px-1 rounded border"># @output</code> names in the code — else you'll get <code className="bg-white px-1 rounded border">name '…' is not defined</code>.</li>
                <li>Right-click a geometry input (e.g. <code className="bg-white px-1 rounded border">Srf</code>) → <span className="font-medium">Type hint → Brep</span>.</li>
                <li>Add a slider to each numeric input (double-click canvas → type a range like <code className="bg-white px-1 rounded border">0..15..30</code> → Enter → wire it).</li>
              </ol>
              <p className="mt-1.5 text-xs text-gray-500">
                Gotchas: the right-click <span className="font-medium">"Name (for humans)"</span> field is only a display
                label, <span className="font-medium">not</span> the variable name. Leave the <code className="bg-white px-1 rounded border">out</code> stream
                (that's the print/console output) — your real outputs are the named ones.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Spec input */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
        <label className="block">
          <span className="text-sm font-medium text-gray-700">What should the component do?</span>
          <textarea
            value={spec}
            onChange={(e) => setSpec(e.target.value)}
            rows={6}
            placeholder="e.g. Generate uniform perforation holes on any curved NURBS surface or Brep — a regular hexagonal lattice with constant edge-to-edge spacing measured in true 3D, boundary offset, optional exact hole count. Inputs: Srf, D, G, B, Seed, Iter, Count. Outputs: points, tangent planes, circles."
            className="mt-1 w-full rounded-lg border border-gray-300 p-3 text-sm font-normal focus:ring-2 focus:ring-brand-400 focus:border-brand-400"
          />
        </label>

        {/* Example chips */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500">Try an example:</span>
          {EXAMPLES.map((ex) => (
            <button
              key={ex.label}
              type="button"
              onClick={() => { setSpec(ex.spec); setErr('') }}
              className="px-2.5 py-1 text-xs rounded-full border border-brand-100 bg-brand-50 text-brand-700 hover:bg-brand-100"
            >
              {ex.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400">
          Tip: name your inputs &amp; outputs, give units, and say "RhinoCommon only" for the cleanest result.
        </p>

        <label className="block">
          <span className="text-sm font-medium text-gray-700">Extra constraints <span className="text-gray-400 font-normal">(optional)</span></span>
          <input
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            placeholder="units, exact input names/types, performance target, RhinoCommon-only, etc."
            className="mt-1 w-full rounded-lg border border-gray-300 p-2.5 text-sm focus:ring-2 focus:ring-brand-400 focus:border-brand-400"
          />
        </label>
        <div className="flex items-center gap-3">
          <button
            onClick={generate}
            disabled={!spec.trim() || busy}
            className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-semibold disabled:opacity-40 flex items-center gap-2"
          >
            {busy ? (
              <>
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Generating…
              </>
            ) : 'Generate script'}
          </button>
          {err && <span className="text-sm text-red-600">{err}</span>}
        </div>
      </div>

      {/* Result */}
      {code && (
        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-500">
              Open the <span className="font-medium">.gh</span> in Grasshopper, or paste the code into a Rhino 8 Python 3 component.
              {model && <span className="ml-2 text-gray-300">· {model}</span>}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={copyCode}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-50"
              >
                {copied ? 'Copied ✓' : 'Copy'}
              </button>
              <button
                onClick={downloadPy}
                className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-600 hover:bg-gray-50"
              >
                Download .py
              </button>
              <button
                onClick={downloadGh}
                className="px-3 py-1.5 bg-brand-600 text-white rounded text-sm font-medium hover:bg-brand-700"
              >
                Download .gh
              </button>
            </div>
          </div>
          {/* Inline button hints */}
          <div className="text-xs text-gray-400 flex flex-wrap gap-x-4 gap-y-1">
            <span><span className="font-medium text-gray-500">.gh</span> — ready component box: open &amp; use</span>
            <span><span className="font-medium text-gray-500">.py</span> / Copy — paste into a Py3 component &amp; wire it up</span>
            <button onClick={() => setShowHelp(true)} className="text-brand-600 hover:underline">Need the steps?</button>
          </div>
          {ghErr && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              {ghErr}
            </p>
          )}
          <textarea
            value={code}
            onChange={(e) => setCode(e.target.value)}
            spellCheck={false}
            rows={22}
            className="w-full rounded-lg border border-gray-200 bg-gray-900 text-gray-100 p-3 font-mono text-xs leading-relaxed focus:ring-2 focus:ring-brand-400"
          />
          <p className="text-xs text-gray-400">
            Review before running — this is AI-generated. Run it once in Rhino 8 to confirm it solves before trusting it. Editing the code above is fine before you copy or download.
          </p>
        </div>
      )}
    </div>
  )
}
